import React, { useId } from 'react';
import { alpha } from '@mui/material/styles';
import { colors } from '../../styles/tokens';

// Deterministic per-category accent so fallback artwork stays on-brand and stable
// across the article page, homepage cards, and headline thumbnails.
export function categoryAccent(category: string): string {
  const map: Record<string, string> = {
    'AI Commerce': colors.accent,
    'Payments': colors.primary,
    'Cross-Border Payments': colors.primary,
    'Cross-Border': colors.primary,
    'VAT & Tax': colors.warning,
    'Logistics & Shipping': colors.violet,
    'Marketplaces': colors.orange,
    'Localization': colors.info,
  };
  if (map[category]) return map[category];
  const palette = [colors.primary, colors.accent, colors.violet, colors.orange, colors.info, colors.warning];
  let hash = 0;
  for (let i = 0; i < category.length; i++) hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length];
}

export interface CategoryArtworkProps {
  category: string;
}

// Brand cart+globe motif rendered as a deterministic SVG, used wherever an
// article has no hero_image_url. Fills its parent; the container controls size.
export const CategoryArtwork: React.FC<CategoryArtworkProps> = ({ category }) => {
  const accent = categoryAccent(category);
  // Unique gradient id per instance — duplicate SVG ids on one page make every
  // reference resolve to the first definition, which would bleed one accent colour
  // across all cards.
  const glowId = `gck-cat-glow-${useId().replace(/:/g, '')}`;
  return (
    <svg
      viewBox="0 0 320 180"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      style={{ display: 'block', backgroundColor: alpha(accent, 0.06) }}
    >
      <defs>
        <radialGradient id={glowId} cx="50%" cy="45%" r="60%">
          <stop offset="0%" stopColor={alpha(accent, 0.18)} />
          <stop offset="100%" stopColor={alpha(accent, 0)} />
        </radialGradient>
      </defs>
      <rect width="320" height="180" fill={`url(#${glowId})`} />
      {/* globe */}
      <circle cx="160" cy="90" r="46" fill="none" stroke={accent} strokeWidth="2" opacity="0.55" />
      <ellipse cx="160" cy="90" rx="20" ry="46" fill="none" stroke={accent} strokeWidth="1.5" opacity="0.4" />
      <ellipse cx="160" cy="90" rx="46" ry="20" fill="none" stroke={accent} strokeWidth="1.5" opacity="0.4" />
      <line x1="114" y1="90" x2="206" y2="90" stroke={accent} strokeWidth="1.5" opacity="0.4" />
      {/* cart */}
      <g stroke={accent} strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M150 80 h40 l-6 26 h-28 z" fill={alpha(accent, 0.12)} />
        <path d="M150 80 l-6 -10 h-8" />
        <circle cx="158" cy="116" r="4" fill={accent} stroke="none" />
        <circle cx="180" cy="116" r="4" fill={accent} stroke="none" />
      </g>
    </svg>
  );
};
